/**
 * Superadmin Service
 * Handles superadmin operations for users and restaurants
 */

import { api } from '@/lib/axios';
import type { AdminListResponse, AdminWithRestaurants, CreateAdminRequest, UpdateAdminRequest } from '@/types/admin';

export const superadminService = {
  // ============================================================================
  // ADMINS
  // ============================================================================

  async getAdmins(): Promise<AdminListResponse> {
    const response = await api.get<AdminListResponse>('/v1/superadmin/admins');
    return response.data;
  },

  async getAdmin(id: string): Promise<{ admin: AdminWithRestaurants }> {
    const response = await api.get<{ admin: AdminWithRestaurants }>(`/v1/superadmin/admins/${id}`);
    return response.data;
  },

  async createAdmin(data: CreateAdminRequest): Promise<{ admin: AdminWithRestaurants }> {
    const response = await api.post<{ admin: AdminWithRestaurants }>('/v1/superadmin/admins', data);
    return response.data;
  },

  async updateAdmin(id: string, data: UpdateAdminRequest): Promise<{ admin: AdminWithRestaurants }> {
    const response = await api.put<{ admin: AdminWithRestaurants }>(`/v1/superadmin/admins/${id}`, data);
    return response.data;
  },

  async deleteAdmin(id: string): Promise<void> {
    await api.delete(`/v1/superadmin/admins/${id}`);
  },

  // ============================================================================
  // RESTAURANTS
  // ============================================================================

  async getRestaurants(): Promise<{ restaurants: Array<{ id: string; name: string }> }> {
    const response = await api.get<{ restaurants: Array<{ id: string; name: string }> }>('/v1/superadmin/restaurants');
    return response.data;
  },

  async getRestaurant(id: string): Promise<{ restaurant: { id: string; name: string } }> {
    const response = await api.get<{ restaurant: { id: string; name: string } }>(`/v1/superadmin/restaurants/${id}`);
    return response.data;
  },

  async createRestaurant(data: { restaurant: { name: string; provider: string } }): Promise<{ restaurant: { id: string; name: string } }> {
    const response = await api.post<{ restaurant: { id: string; name: string } }>('/v1/superadmin/restaurants', data);
    return response.data;
  },

  async updateRestaurant(id: string, data: { restaurant: { name?: string } }): Promise<{ restaurant: { id: string; name: string } }> {
    const response = await api.put<{ restaurant: { id: string; name: string } }>(`/v1/superadmin/restaurants/${id}`, data);
    return response.data;
  },
};
